import type { GeoCoord, Point2D } from './mapGeometry';
import { projectToLocalMeters } from './mapGeometry';
import type { AggregationLapResult, AggregationPoint } from '@/types/telemetry';
import type { SplineSample, TrackPoint } from '@/lib/spline';

/**
 * Precomputed 256-entry thermal RGBA lookup table for GPS driving line density.
 * Maps normalized density [0..255] -> [R, G, B, A]:
 *  - Low density (single outlier lap / outer fringe): translucent sky blue -> cyan (#0ea5e9 -> #06b6d4)
 *  - Medium-low density (2-3 overlapping laps): emerald green (#22c55e)
 *  - Medium-high density (4-6 overlapping laps): warm yellow (#facc15)
 *  - High density (primary racing groove): hot orange (#f97316) -> crimson red (#ef4444)
 */
const HEATMAP_LUT: Uint8ClampedArray = (() => {
  const lut = new Uint8ClampedArray(256 * 4);
  const stops: Array<{ t: number; r: number; g: number; b: number; a: number }> = [
    { t: 0.0, r: 14, g: 165, b: 233, a: 0 },
    { t: 0.05, r: 14, g: 165, b: 233, a: 95 },
    { t: 0.18, r: 6, g: 182, b: 212, a: 175 },
    { t: 0.40, r: 34, g: 197, b: 94, a: 210 },
    { t: 0.65, r: 250, g: 204, b: 21, a: 232 },
    { t: 0.84, r: 249, g: 115, b: 22, a: 246 },
    { t: 1.0, r: 239, g: 68, b: 68, a: 255 },
  ];

  for (let i = 1; i < 256; i++) {
    const t = i / 255;
    let s0 = stops[0];
    let s1 = stops[stops.length - 1];
    for (let j = 0; j < stops.length - 1; j++) {
      if (t >= stops[j].t && t <= stops[j + 1].t) {
        s0 = stops[j];
        s1 = stops[j + 1];
        break;
      }
    }
    const span = s1.t - s0.t;
    const u = span > 1e-6 ? (t - s0.t) / span : 0;
    const base = i * 4;
    lut[base] = Math.round(s0.r + u * (s1.r - s0.r));
    lut[base + 1] = Math.round(s0.g + u * (s1.g - s0.g));
    lut[base + 2] = Math.round(s0.b + u * (s1.b - s0.b));
    lut[base + 3] = Math.round(s0.a + u * (s1.a - s0.a));
  }
  return lut;
})();

let sharedOffscreenCanvas: HTMLCanvasElement | null = null;

function getOffscreenCanvas(width: number, height: number): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  if (!sharedOffscreenCanvas) {
    sharedOffscreenCanvas = document.createElement('canvas');
  }
  if (sharedOffscreenCanvas.width !== width || sharedOffscreenCanvas.height !== height) {
    sharedOffscreenCanvas.width = width;
    sharedOffscreenCanvas.height = height;
  }
  return sharedOffscreenCanvas;
}

/**
 * Renders a spatial density heat map of all laps' GPS trajectories onto `ctx`.
 *
 * IMPORTANT: The offscreen canvas is first filled with opaque black (`#000000`, A=255)
 * before accumulating strokes with `globalCompositeOperation = 'lighter'` and opaque
 * `rgb(rStep, 0, 0)` colors. If the canvas were transparent (`A=0`), HTML5 `getImageData()`
 * would un-premultiply RGB by dividing by A, turning every non-zero pixel into `R=255`
 * and destroying the density gradient.
 */
export function drawGpsLineHeatmap(
  ctx: CanvasRenderingContext2D,
  laps: AggregationLapResult[],
  anchor: GeoCoord,
  toCanvas: (pt: Point2D) => Point2D,
  scale: number,
  width: number,
  height: number
): void {
  const validLaps = laps.filter(l => l.gpsPoints && l.gpsPoints.length >= 2);
  if (validLaps.length === 0 || width <= 0 || height <= 0) return;

  const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  const bw = Math.max(1, Math.round(width * dpr));
  const bh = Math.max(1, Math.round(height * dpr));
  const offCanvas = getOffscreenCanvas(bw, bh);
  const offCtx = offCanvas?.getContext('2d', { willReadFrequently: true });

  // Thin, physically scaled stroke widths so individual lines and lateral spread remain sharp
  const outerWidthPx = Math.max(1.6, Math.min(4.2, scale * 0.45));
  const midWidthPx = Math.max(1.0, Math.min(2.6, scale * 0.28));
  const coreWidthPx = Math.max(0.55, Math.min(1.3, scale * 0.14));

  if (!offCanvas || !offCtx) {
    ctx.save();
    const alpha = Math.max(0.08, Math.min(0.35, 1.5 / validLaps.length));
    ctx.strokeStyle = `rgba(6, 182, 212, ${alpha})`;
    ctx.lineWidth = midWidthPx;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const lap of validLaps) {
      const pts = lap.gpsPoints!;
      ctx.beginPath();
      for (let i = 0; i < pts.length; i++) {
        const cp = toCanvas(projectToLocalMeters({ lat: pts[i].latitudeRaw / 1e6, lng: pts[i].longitudeRaw / 1e6 }, anchor));
        if (i === 0) ctx.moveTo(cp.x, cp.y);
        else ctx.lineTo(cp.x, cp.y);
      }
      ctx.stroke();
    }
    ctx.restore();
    return;
  }

  // 1. Fill offscreen buffer with opaque black (A=255) so getImageData() never un-premultiplies R by A
  offCtx.setTransform(1, 0, 0, 1, 0, 0);
  offCtx.globalCompositeOperation = 'source-over';
  offCtx.fillStyle = '#000000';
  offCtx.fillRect(0, 0, bw, bh);

  // 2. Accumulate lap trajectories in CSS coordinate space scaled by DPR
  offCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  offCtx.globalCompositeOperation = 'lighter';
  offCtx.lineCap = 'round';
  offCtx.lineJoin = 'round';

  const cappedLaps = validLaps.slice(0, 45);
  const n = cappedLaps.length;

  // Choose integer Red increments per pass so `n` overlapping laps utilize [1..225] without clipping 255
  const perLapTotal = Math.max(6, Math.min(60, Math.floor(225 / Math.max(1, n))));
  const rOuter = Math.max(2, Math.round(perLapTotal * 0.18));
  const rMid = Math.max(2, Math.round(perLapTotal * 0.32));
  const rCore = Math.max(2, perLapTotal - rOuter - rMid);

  const outerColor = `rgb(${rOuter}, 0, 0)`;
  const midColor = `rgb(${rMid}, 0, 0)`;
  const coreColor = `rgb(${rCore}, 0, 0)`;

  for (const lap of cappedLaps) {
    const pts = lap.gpsPoints!;
    offCtx.beginPath();
    for (let i = 0; i < pts.length; i++) {
      const cp = toCanvas(
        projectToLocalMeters(
          { lat: pts[i].latitudeRaw / 1e6, lng: pts[i].longitudeRaw / 1e6 },
          anchor
        )
      );
      if (i === 0) offCtx.moveTo(cp.x, cp.y);
      else offCtx.lineTo(cp.x, cp.y);
    }

    // 3-pass Gaussian-like profile per lap: soft outer fringe -> mid body -> tight core
    offCtx.lineWidth = outerWidthPx;
    offCtx.strokeStyle = outerColor;
    offCtx.stroke();

    offCtx.lineWidth = midWidthPx;
    offCtx.strokeStyle = midColor;
    offCtx.stroke();

    offCtx.lineWidth = coreWidthPx;
    offCtx.strokeStyle = coreColor;
    offCtx.stroke();
  }

  const imgData = offCtx.getImageData(0, 0, bw, bh);
  const data = imgData.data;

  // Build 256-bin histogram of non-zero Red values to find the 98th percentile density
  const hist = new Uint32Array(256);
  let nonZeroCount = 0;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    if (r > 0) {
      hist[r]++;
      nonZeroCount++;
    }
  }

  if (nonZeroCount === 0) return;

  const targetRank = Math.floor(nonZeroCount * 0.98);
  let cumulative = 0;
  let p98 = 1;
  for (let r = 1; r < 256; r++) {
    cumulative += hist[r];
    if (cumulative >= targetRank) {
      p98 = r;
      break;
    }
  }

  // Ensure a single isolated lap stays in the cool blue/cyan range (~0.18-0.25) rather than normalizing to 1.0
  const minRef = n === 1 ? perLapTotal * 2.5 : perLapTotal * Math.min(n, 3.2);
  const refMax = Math.max(p98, minRef, 1);

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    if (r === 0) {
      data[i + 3] = 0;
      continue;
    }
    const norm = Math.min(1, r / refMax);
    const curved = Math.pow(norm, 0.85);
    const lutIdx = Math.min(255, Math.max(1, Math.round(curved * 255))) * 4;
    data[i] = HEATMAP_LUT[lutIdx];
    data[i + 1] = HEATMAP_LUT[lutIdx + 1];
    data[i + 2] = HEATMAP_LUT[lutIdx + 2];
    data[i + 3] = HEATMAP_LUT[lutIdx + 3];
  }

  offCtx.putImageData(imgData, 0, 0);
  ctx.drawImage(offCanvas, 0, 0, width, height);
}

/**
 * Draws the selected lap's full GPS racing line as a crisp, thin dashed white trace
 * so the underlying thermal heat map gradient remains visible through the dashes.
 */
export function drawSelectedLapGpsLine(
  ctx: CanvasRenderingContext2D,
  gpsPoints: AggregationPoint[],
  anchor: GeoCoord,
  toCanvas: (pt: Point2D) => Point2D
): void {
  if (!gpsPoints || gpsPoints.length < 2) return;

  ctx.save();
  ctx.beginPath();
  for (let i = 0; i < gpsPoints.length; i++) {
    const cp = toCanvas(
      projectToLocalMeters(
        { lat: gpsPoints[i].latitudeRaw / 1e6, lng: gpsPoints[i].longitudeRaw / 1e6 },
        anchor
      )
    );
    if (i === 0) ctx.moveTo(cp.x, cp.y);
    else ctx.lineTo(cp.x, cp.y);
  }
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.setLineDash([6, 5]);

  // Subtle dark outer casing for contrast over bright yellow/red heatmap cores
  ctx.strokeStyle = 'rgba(10, 10, 15, 0.8)';
  ctx.lineWidth = 2.6;
  ctx.stroke();

  // Crisp thin white racing line core
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.4;
  ctx.stroke();
  ctx.restore();
}

/**
 * Renders a two-color differential spatial density heat map comparing Group A vs Group B GPS lines.
 *
 * - Accumulates Group A trajectories into the Red channel (`rgb(rStep, 0, 0)`) and Group B
 *   trajectories into the Green channel (`rgb(0, gStep, 0)`) on an opaque `#000000` (A=255)
 *   offscreen canvas using `globalCompositeOperation = 'lighter'`.
 * - Normalizes each channel independently by its 98th percentile density so groups with
 *   different lap counts are weighted fairly.
 * - Pixels where both groups overlap equally (`normA ≈ normB`) fade to a subtle neutral slate trace,
 *   while Group A-dominant lines glow Cyan (`#06b6d4` -> `#22d3ee`) and Group B-dominant lines
 *   glow Orange (`#f97316` -> `#fb923c`).
 */
export function drawGpsLineDiffHeatmap(
  ctx: CanvasRenderingContext2D,
  lapsA: AggregationLapResult[],
  lapsB: AggregationLapResult[],
  anchor: GeoCoord,
  toCanvas: (pt: Point2D) => Point2D,
  scale: number,
  width: number,
  height: number,
  segmentRange?: [number, number] | null,
  trackOptions?: FasterSegmentsTrackOptions
): void {
  const validA = (lapsA || []).filter(l => l.gpsPoints && l.gpsPoints.length >= 2);
  const validB = (lapsB || []).filter(l => l.gpsPoints && l.gpsPoints.length >= 2);
  if ((validA.length === 0 && validB.length === 0) || width <= 0 || height <= 0) return;

  const hasRange = segmentRange != null;
  const sMin = hasRange ? Math.min(segmentRange[0], segmentRange[1]) : 0.0;
  const sMax = hasRange ? Math.max(segmentRange[0], segmentRange[1]) : 1.0;

  const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  const bw = Math.max(1, Math.round(width * dpr));
  const bh = Math.max(1, Math.round(height * dpr));
  const offCanvas = getOffscreenCanvas(bw, bh);
  const offCtx = offCanvas?.getContext('2d', { willReadFrequently: true });

  const outerWidthPx = Math.max(1.8, Math.min(4.6, scale * 0.48));
  const midWidthPx = Math.max(1.1, Math.min(2.8, scale * 0.30));
  const coreWidthPx = Math.max(0.6, Math.min(1.4, scale * 0.15));

  if (!offCanvas || !offCtx) {
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = midWidthPx;
    if (validA.length > 0) {
      ctx.strokeStyle = `rgba(239, 68, 68, ${Math.max(0.1, Math.min(0.4, 1.5 / validA.length))})`;
      for (const lap of validA) {
        const pts = lap.gpsPoints!;
        const iStart = hasRange ? Math.max(0, Math.floor(sMin * (pts.length - 1))) : 0;
        const iEnd = hasRange ? Math.min(pts.length - 1, Math.ceil(sMax * (pts.length - 1))) : pts.length - 1;
        if (iEnd <= iStart) continue;
        ctx.beginPath();
        for (let i = iStart; i <= iEnd; i++) {
          const cp = toCanvas(projectToLocalMeters({ lat: pts[i].latitudeRaw / 1e6, lng: pts[i].longitudeRaw / 1e6 }, anchor));
          if (i === iStart) ctx.moveTo(cp.x, cp.y);
          else ctx.lineTo(cp.x, cp.y);
        }
        ctx.stroke();
      }
    }
    if (validB.length > 0) {
      ctx.strokeStyle = `rgba(59, 130, 246, ${Math.max(0.1, Math.min(0.4, 1.5 / validB.length))})`;
      for (const lap of validB) {
        const pts = lap.gpsPoints!;
        const iStart = hasRange ? Math.max(0, Math.floor(sMin * (pts.length - 1))) : 0;
        const iEnd = hasRange ? Math.min(pts.length - 1, Math.ceil(sMax * (pts.length - 1))) : pts.length - 1;
        if (iEnd <= iStart) continue;
        ctx.beginPath();
        for (let i = iStart; i <= iEnd; i++) {
          const cp = toCanvas(projectToLocalMeters({ lat: pts[i].latitudeRaw / 1e6, lng: pts[i].longitudeRaw / 1e6 }, anchor));
          if (i === iStart) ctx.moveTo(cp.x, cp.y);
          else ctx.lineTo(cp.x, cp.y);
        }
        ctx.stroke();
      }
    }
    ctx.restore();
    return;
  }

  // 1. Fill offscreen buffer with opaque black (A=255)
  offCtx.setTransform(1, 0, 0, 1, 0, 0);
  offCtx.globalCompositeOperation = 'source-over';
  offCtx.fillStyle = '#000000';
  offCtx.fillRect(0, 0, bw, bh);

  // 2. Accumulate Group A into Red and Group B into Green
  offCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  offCtx.globalCompositeOperation = 'lighter';
  offCtx.lineCap = 'round';
  offCtx.lineJoin = 'round';

  const cappedA = validA.slice(0, 45);
  const cappedB = validB.slice(0, 45);

  const accumulateGroup = (laps: AggregationLapResult[], channel: 'r' | 'g') => {
    const n = laps.length;
    if (n === 0) return { n: 0, perLapTotal: 1 };
    const perLapTotal = Math.max(6, Math.min(60, Math.floor(225 / Math.max(1, n))));
    const cOuter = Math.max(2, Math.round(perLapTotal * 0.18));
    const cMid = Math.max(2, Math.round(perLapTotal * 0.32));
    const cCore = Math.max(2, perLapTotal - cOuter - cMid);

    const outerColor = channel === 'r' ? `rgb(${cOuter}, 0, 0)` : `rgb(0, ${cOuter}, 0)`;
    const midColor = channel === 'r' ? `rgb(${cMid}, 0, 0)` : `rgb(0, ${cMid}, 0)`;
    const coreColor = channel === 'r' ? `rgb(${cCore}, 0, 0)` : `rgb(0, ${cCore}, 0)`;

    for (const lap of laps) {
      const pts = lap.gpsPoints!;
      const iStart = hasRange ? Math.max(0, Math.floor(sMin * (pts.length - 1))) : 0;
      const iEnd = hasRange ? Math.min(pts.length - 1, Math.ceil(sMax * (pts.length - 1))) : pts.length - 1;
      if (iEnd <= iStart) continue;

      offCtx.beginPath();
      for (let i = iStart; i <= iEnd; i++) {
        const cp = toCanvas(
          projectToLocalMeters(
            { lat: pts[i].latitudeRaw / 1e6, lng: pts[i].longitudeRaw / 1e6 },
            anchor
          )
        );
        if (i === iStart) offCtx.moveTo(cp.x, cp.y);
        else offCtx.lineTo(cp.x, cp.y);
      }

      offCtx.lineWidth = outerWidthPx;
      offCtx.strokeStyle = outerColor;
      offCtx.stroke();

      offCtx.lineWidth = midWidthPx;
      offCtx.strokeStyle = midColor;
      offCtx.stroke();

      offCtx.lineWidth = coreWidthPx;
      offCtx.strokeStyle = coreColor;
      offCtx.stroke();
    }
    return { n, perLapTotal };
  };

  const infoA = accumulateGroup(cappedA, 'r');
  const infoB = accumulateGroup(cappedB, 'g');

  const imgData = offCtx.getImageData(0, 0, bw, bh);
  const data = imgData.data;

  const histA = new Uint32Array(256);
  const histB = new Uint32Array(256);
  let countA = 0;
  let countB = 0;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    if (r > 0) {
      histA[r]++;
      countA++;
    }
    if (g > 0) {
      histB[g]++;
      countB++;
    }
  }

  if (countA === 0 && countB === 0) return;

  const computeP98 = (hist: Uint32Array, count: number): number => {
    if (count === 0) return 1;
    const target = Math.floor(count * 0.98);
    let cum = 0;
    for (let v = 1; v < 256; v++) {
      cum += hist[v];
      if (cum >= target) return v;
    }
    return 1;
  };

  const p98A = computeP98(histA, countA);
  const p98B = computeP98(histB, countB);
  const minRefA = infoA.n <= 1 ? infoA.perLapTotal * 1.4 : infoA.perLapTotal * Math.min(infoA.n, 2.6);
  const minRefB = infoB.n <= 1 ? infoB.perLapTotal * 1.4 : infoB.perLapTotal * Math.min(infoB.n, 2.6);
  const refMaxA = Math.max(p98A, minRefA, 1);
  const refMaxB = Math.max(p98B, minRefB, 1);

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    if (r === 0 && g === 0) {
      data[i + 3] = 0;
      continue;
    }

    const normA = r > 0 ? Math.min(1, r / refMaxA) : 0;
    const normB = g > 0 ? Math.min(1, g / refMaxB) : 0;
    const maxDensity = Math.max(normA, normB);
    const diff = normA - normB; // [-1..1], >0 means Group A dominant, <0 means Group B dominant

    // Relative dominance: 0 when normA == normB (shared line), 1 when exclusively in one group
    const relDominance = Math.min(1, Math.abs(diff) / (Math.max(maxDensity, 0.08) * 0.75));
    const curvedDensity = Math.pow(maxDensity, 0.82);

    // Base neutral shared trace color: cool slate-gray (#94a3b8)
    const neutralR = 148;
    const neutralG = 163;
    const neutralB = 184;

    // Dominant group color:
    // Group A -> Red (#ef4444 -> #f87171)
    // Group B -> Blue (#3b82f6 -> #60a5fa)
    let domR: number;
    let domG: number;
    let domB: number;
    if (diff >= 0) {
      domR = Math.round(239 + curvedDensity * (248 - 239));
      domG = Math.round(68 + curvedDensity * (113 - 68));
      domB = Math.round(68 + curvedDensity * (113 - 68));
    } else {
      domR = Math.round(59 + curvedDensity * (96 - 59));
      domG = Math.round(130 + curvedDensity * (165 - 130));
      domB = Math.round(246 + curvedDensity * (250 - 246));
    }

    // Blend from neutral slate (shared line) to vibrant Red/Blue (divergent line)
    data[i] = Math.round(neutralR + relDominance * (domR - neutralR));
    data[i + 1] = Math.round(neutralG + relDominance * (domG - neutralG));
    data[i + 2] = Math.round(neutralB + relDominance * (domB - neutralB));

    // Shared lines fade to subtle background alpha (~22..75), while divergent lines pop (~90..248)
    const sharedAlpha = 22 + curvedDensity * 55;
    const diffAlpha = 85 + curvedDensity * 165;
    data[i + 3] = Math.min(255, Math.round(sharedAlpha + relDominance * (diffAlpha - sharedAlpha)));
  }

  offCtx.putImageData(imgData, 0, 0);

  const splineSamples = trackOptions?.splineSamples;
  const totalLengthM = trackOptions?.totalLengthM ?? 0;
  const finishDistM = trackOptions?.finishDistM ?? 0;

  if (hasRange && splineSamples && splineSamples.length >= 3 && totalLengthM > 0) {
    ctx.save();
    const spanPct = Math.max(0.005, sMax - sMin);
    const numSteps = Math.max(8, Math.ceil(spanPct * 200));
    const innerClip: Point2D[] = [];
    const outerClip: Point2D[] = [];

    for (let i = 0; i <= numSteps; i++) {
      const s = sMin + (i / numSteps) * (sMax - sMin);
      const absD = (finishDistM + s * totalLengthM) % totalLengthM;
      const sample = sampleTrackRibbonAtDistance(absD, splineSamples, totalLengthM);
      const clipHalfWidth = Math.max(12.0, (sample.widthM || 8.0) * 1.35);
      const pIn = offsetRibbonPoint(sample, clipHalfWidth);
      const pOut = offsetRibbonPoint(sample, -clipHalfWidth);
      innerClip.push(toCanvas(projectToLocalMeters(pIn, anchor)));
      outerClip.push(toCanvas(projectToLocalMeters(pOut, anchor)));
    }

    ctx.beginPath();
    innerClip.forEach((p, idx) => {
      if (idx === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    });
    for (let idx = outerClip.length - 1; idx >= 0; idx--) {
      ctx.lineTo(outerClip[idx].x, outerClip[idx].y);
    }
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(offCanvas, 0, 0, width, height);
    ctx.restore();
  } else {
    ctx.drawImage(offCanvas, 0, 0, width, height);
  }
}

export type MedianLinePoint = {
  lat?: number;
  lng?: number;
  latitudeRaw?: number;
  longitudeRaw?: number;
};

/**
 * Draws clean median driving lines for Group A (Red #ef4444) and Group B (Blue #3b82f6),
 * plus a subtle shaded ribbon between them so line cross-overs and apex differences stand out.
 */
export function drawMedianLineComparison(
  ctx: CanvasRenderingContext2D,
  medianLineA: MedianLinePoint[],
  medianLineB: MedianLinePoint[],
  anchor: GeoCoord,
  toCanvas: (pt: Point2D) => Point2D
): void {
  const hasA = medianLineA && medianLineA.length >= 3;
  const hasB = medianLineB && medianLineB.length >= 3;
  if (!hasA && !hasB) return;

  const toGeo = (pt: MedianLinePoint): GeoCoord => ({
    lat: pt.lat !== undefined ? pt.lat : (pt.latitudeRaw ?? 0) / 1e6,
    lng: pt.lng !== undefined ? pt.lng : (pt.longitudeRaw ?? 0) / 1e6,
  });

  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const ptsA = hasA ? medianLineA.map(pt => toCanvas(projectToLocalMeters(toGeo(pt), anchor))) : [];
  const ptsB = hasB ? medianLineB.map(pt => toCanvas(projectToLocalMeters(toGeo(pt), anchor))) : [];

  // If both lines have matching station counts, shade the divergence ribbon between them
  if (ptsA.length > 0 && ptsA.length === ptsB.length) {
    const n = ptsA.length;
    ctx.beginPath();
    for (let i = 0; i <= n; i++) {
      const p = ptsA[i % n];
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    }
    for (let i = n; i >= 0; i--) {
      const p = ptsB[i % n];
      ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.fillStyle = 'rgba(168, 85, 247, 0.22)';
    ctx.fill();
  }

  const strokeClosedLine = (pts: Point2D[], color: string, dashed: boolean) => {
    if (pts.length < 2) return;
    ctx.beginPath();
    for (let i = 0; i <= pts.length; i++) {
      const p = pts[i % pts.length];
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    }
    ctx.setLineDash(dashed ? [6, 4] : []);
    ctx.strokeStyle = 'rgba(10, 10, 15, 0.75)';
    ctx.lineWidth = 3.2;
    ctx.stroke();

    ctx.strokeStyle = color;
    ctx.lineWidth = 1.8;
    ctx.stroke();
  };

  // Group A: solid Red (#ef4444), Group B: dashed Blue (#3b82f6)
  strokeClosedLine(ptsA, '#ef4444', false);
  strokeClosedLine(ptsB, '#3b82f6', true);

  ctx.restore();
}

export interface FasterSegmentsTrackOptions {
  splineSamples?: SplineSample[];
  totalLengthM?: number;
  finishDistM?: number;
  /** Optional per-station metric advantage where >0 means Group A is better (Red) and <0 means Group B is better (Blue). */
  stationAdvantages?: number[];
}

function sampleTrackRibbonAtDistance(
  targetDistM: number,
  samples: SplineSample[],
  totalLengthM: number
): { point: TrackPoint; normal: { x: number; y: number }; widthM: number } {
  const n = samples.length;
  if (n === 0) return { point: { lat: 0, lng: 0 }, normal: { x: 0, y: 1 }, widthM: 8.0 };
  const totalLen = totalLengthM > 0 ? totalLengthM : 1.0;
  const d = ((targetDistM % totalLen) + totalLen) % totalLen;

  let low = 0;
  let high = n - 1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (samples[mid].distanceM <= d) {
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  const i0 = Math.max(0, Math.min(n - 1, high));
  const i1 = (i0 + 1) % n;
  const s0 = samples[i0];
  const s1 = samples[i1];

  const d0 = s0.distanceM;
  const d1 = i1 === 0 ? totalLen : s1.distanceM;
  const span = d1 - d0;
  const u = span > 1e-6 ? Math.max(0, Math.min(1, (d - d0) / span)) : 0;

  const lat = s0.point.lat + u * (s1.point.lat - s0.point.lat);
  const lng = s0.point.lng + u * (s1.point.lng - s0.point.lng);
  const nx = s0.normal.x + u * (s1.normal.x - s0.normal.x);
  const ny = s0.normal.y + u * (s1.normal.y - s0.normal.y);
  const nLen = Math.sqrt(nx * nx + ny * ny) || 1;
  const widthM = s0.widthM + u * (s1.widthM - s0.widthM);

  return {
    point: { lat, lng },
    normal: { x: nx / nLen, y: ny / nLen },
    widthM,
  };
}

function offsetRibbonPoint(
  sample: { point: TrackPoint; normal: { x: number; y: number } },
  offsetM: number
): GeoCoord {
  const latRad = (sample.point.lat * Math.PI) / 180;
  const cosLat = Math.cos(latRad) || 1;
  const dLat = ((sample.normal.y * offsetM) / 6371000.0) * (180 / Math.PI);
  const dLon = ((sample.normal.x * offsetM) / (6371000.0 * cosLat)) * (180 / Math.PI);
  return {
    lat: sample.point.lat + dLat,
    lng: sample.point.lng + dLon,
  };
}

/**
 * Highlights faster/better segments directly on the track map ribbon (behind driving line heatmaps
 * and event zone overlays). Each station-to-station segment is prominently tinted across the full
 * track width by which aggregation has the advantage (Group A = Red `#ef4444`,
 * Group B = Blue `#3b82f6`) so faster and slower ranges are clearly distinguishable over satellite imagery.
 */
export function drawFasterSegmentsOverlay(
  ctx: CanvasRenderingContext2D,
  medianLineA: MedianLinePoint[],
  medianLineB: MedianLinePoint[],
  stationSpeedAKmh: number[],
  stationSpeedBKmh: number[],
  anchor: GeoCoord,
  toCanvas: (pt: Point2D) => Point2D,
  segmentRange?: [number, number] | null,
  trackOptions?: FasterSegmentsTrackOptions
): void {
  const customAdv = trackOptions?.stationAdvantages;
  const nSpeeds = Math.min(
    stationSpeedAKmh?.length || 0,
    stationSpeedBKmh?.length || 0
  );
  const nLines = Math.min(
    medianLineA?.length || 0,
    medianLineB?.length || 0
  );
  const n =
    customAdv && customAdv.length >= 3
      ? customAdv.length
      : nSpeeds >= 3
      ? nLines >= 3
        ? Math.min(nSpeeds, nLines)
        : nSpeeds
      : 0;
  if (n < 3) return;

  const hasRange = segmentRange != null;
  const sMin = hasRange ? Math.min(segmentRange[0], segmentRange[1]) : 0.0;
  const sMax = hasRange ? Math.max(segmentRange[0], segmentRange[1]) : 1.0;

  // Compute per-segment difference (diff > 0 means Group B better, diff < 0 means Group A better)
  const segDiffs: number[] = new Array(n);
  let maxAbsDiff = 0;
  for (let k = 0; k < n; k++) {
    const next = (k + 1) % n;
    let diff: number;
    if (customAdv && customAdv.length === n) {
      diff = -customAdv[next]; // customAdv > 0 means A better -> diff < 0
    } else {
      const vA = (stationSpeedAKmh[k] + stationSpeedAKmh[next]) * 0.5;
      const vB = (stationSpeedBKmh[k] + stationSpeedBKmh[next]) * 0.5;
      diff = vB - vA; // >0 means Group B faster, <0 means Group A faster
    }
    segDiffs[k] = diff;
    const absD = Math.abs(diff);
    if (absD > maxAbsDiff) {
      maxAbsDiff = absD;
    }
  }
  if (maxAbsDiff < 1e-4) maxAbsDiff = 1.0;

  const rgbA: [number, number, number] = [239, 68, 68]; // Red (#ef4444)
  const rgbB: [number, number, number] = [59, 130, 246]; // Blue (#3b82f6)

  const splineSamples = trackOptions?.splineSamples;
  const totalLengthM = trackOptions?.totalLengthM ?? 0;
  const finishDistM = trackOptions?.finishDistM ?? 0;

  ctx.save();

  // Primary mode: Highlight directly on the canonical track map ribbon
  if (splineSamples && splineSamples.length >= 3 && totalLengthM > 0) {
    const subSteps = 2;
    const totalCrossSections = n * subSteps;
    const innerPts: Point2D[] = new Array(totalCrossSections);
    const outerPts: Point2D[] = new Array(totalCrossSections);

    for (let m = 0; m < totalCrossSections; m++) {
      const distFromFinish = (m / totalCrossSections) * totalLengthM;
      const absD = (finishDistM + distFromFinish) % totalLengthM;
      const sample = sampleTrackRibbonAtDistance(absD, splineSamples, totalLengthM);
      const hw = (sample.widthM || 8.0) * 0.5;
      const innerGeo = offsetRibbonPoint(sample, hw);
      const outerGeo = offsetRibbonPoint(sample, -hw);
      innerPts[m] = toCanvas(projectToLocalMeters(innerGeo, anchor));
      outerPts[m] = toCanvas(projectToLocalMeters(outerGeo, anchor));
    }

    ctx.lineJoin = 'round';
    ctx.lineCap = 'butt';

    for (let k = 0; k < n; k++) {
      const s = (k + 0.5) / n;
      if (hasRange && (s < sMin || s > sMax)) {
        continue;
      }

      const diff = segDiffs[k];
      const intensity = Math.min(Math.abs(diff) / maxAbsDiff, 1.0);
      if (intensity < 0.015) continue;

      const winnerRgb = diff >= 0 ? rgbB : rgbA;
      const fillAlpha = 0.14 + 0.34 * Math.pow(intensity, 0.65);
      const borderAlpha = Math.min(0.65, fillAlpha * 1.45);
      const fillStyle = `rgba(${winnerRgb[0]}, ${winnerRgb[1]}, ${winnerRgb[2]}, ${fillAlpha.toFixed(3)})`;
      const edgeStyle = `rgba(${winnerRgb[0]}, ${winnerRgb[1]}, ${winnerRgb[2]}, ${borderAlpha.toFixed(3)})`;

      const m0 = k * subSteps;
      const m1 = (m0 + 1) % totalCrossSections;
      const m2 = (m0 + 2) % totalCrossSections;

      ctx.beginPath();
      ctx.moveTo(innerPts[m0].x, innerPts[m0].y);
      ctx.lineTo(innerPts[m1].x, innerPts[m1].y);
      ctx.lineTo(innerPts[m2].x, innerPts[m2].y);
      ctx.lineTo(outerPts[m2].x, outerPts[m2].y);
      ctx.lineTo(outerPts[m1].x, outerPts[m1].y);
      ctx.lineTo(outerPts[m0].x, outerPts[m0].y);
      ctx.closePath();

      ctx.fillStyle = fillStyle;
      ctx.fill();
      // Subtle seam stroke to prevent hairline canvas anti-aliasing gaps between segments
      ctx.strokeStyle = fillStyle;
      ctx.lineWidth = 0.75;
      ctx.stroke();

      // Gentle inner and outer track edge tint
      ctx.beginPath();
      ctx.moveTo(innerPts[m0].x, innerPts[m0].y);
      ctx.lineTo(innerPts[m1].x, innerPts[m1].y);
      ctx.lineTo(innerPts[m2].x, innerPts[m2].y);
      ctx.moveTo(outerPts[m0].x, outerPts[m0].y);
      ctx.lineTo(outerPts[m1].x, outerPts[m1].y);
      ctx.lineTo(outerPts[m2].x, outerPts[m2].y);
      ctx.strokeStyle = edgeStyle;
      ctx.lineWidth = 1.25;
      ctx.stroke();
    }

    ctx.restore();
    return;
  }

  // Fallback when track shape is unavailable: draw a wide, soft translucent corridor along the track
  if (nLines < 3) {
    ctx.restore();
    return;
  }

  const toGeo = (pt: MedianLinePoint): GeoCoord => ({
    lat: pt.lat !== undefined ? pt.lat : (pt.latitudeRaw ?? 0) / 1e6,
    lng: pt.lng !== undefined ? pt.lng : (pt.longitudeRaw ?? 0) / 1e6,
  });

  const midPts: Point2D[] = new Array(n);
  for (let k = 0; k < n; k++) {
    const gA = toGeo(medianLineA[k]);
    const gB = toGeo(medianLineB[k]);
    const midGeo: GeoCoord = {
      lat: (gA.lat + gB.lat) * 0.5,
      lng: (gA.lng + gB.lng) * 0.5,
    };
    midPts[k] = toCanvas(projectToLocalMeters(midGeo, anchor));
  }

  ctx.lineCap = 'butt';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 12.0;

  for (let k = 0; k < n; k++) {
    const next = (k + 1) % n;
    const p0 = midPts[k];
    const p1 = midPts[next];
    const s = (k + 0.5) / n;

    if (hasRange && (s < sMin || s > sMax)) {
      continue;
    }

    const diff = segDiffs[k];
    const intensity = Math.min(Math.abs(diff) / maxAbsDiff, 1.0);
    if (intensity < 0.015) continue;

    const winnerRgb = diff >= 0 ? rgbB : rgbA;
    const alpha = 0.14 + 0.34 * Math.pow(intensity, 0.65);

    ctx.beginPath();
    ctx.moveTo(p0.x, p0.y);
    ctx.lineTo(p1.x, p1.y);
    ctx.strokeStyle = `rgba(${winnerRgb[0]}, ${winnerRgb[1]}, ${winnerRgb[2]}, ${alpha.toFixed(3)})`;
    ctx.stroke();
  }

  ctx.restore();
}


